import React, { useState, useEffect } from 'react';
import './SubscriptionRenewal.css';
import collegeConfig from '../../config/collegeConfig';
import { Capacitor } from '@capacitor/core';

const isNativeApp = Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'web';
// Web root URL — used to redirect native app users to pay in browser
const webUrl = collegeConfig.api.baseURL.replace(/\/api$/, '');

const loadRazorpay = () =>
  new Promise(resolve => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const SubscriptionRenewal = ({ user, onRenewed, onLogout }) => {
  const [settings, setSettings] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${collegeConfig.api.baseURL}/payment/settings`)
      .then(r => r.json())
      .then(data => { if (data.success) setSettings(data.settings); })
      .catch(() => {});
  }, []);

  const handleRenew = async () => {
    setProcessing(true);
    setError('');

    try {
      const loaded = await loadRazorpay();
      if (!loaded) {
        setError('Failed to load payment gateway. Check your internet connection.');
        setProcessing(false);
        return;
      }

      const token = localStorage.getItem('token');
      const orderRes = await fetch(`${collegeConfig.api.baseURL}/payment/create-renewal-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const orderData = await orderRes.json();

      if (!orderData.success) {
        setError(orderData.message || 'Failed to create renewal order');
        setProcessing(false);
        return;
      }

      const options = {
        key: orderData.key,
        amount: orderData.order.amount,
        currency: 'INR',
        name: collegeConfig.collegeName,
        description: 'E-Logbook Subscription Renewal',
        image: collegeConfig.logo,
        order_id: orderData.order.id,
        prefill: {
          name: user?.profile ? `${user.profile.firstName} ${user.profile.lastName}` : '',
          email: user?.email || '',
          contact: user?.profile?.phone || ''
        },
        theme: { color: collegeConfig.theme?.primaryColor || '#b11217' },
        handler: async paymentResponse => {
          try {
            const verifyRes = await fetch(`${collegeConfig.api.baseURL}/payment/verify-renewal`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature
              })
            });
            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              if (onRenewed) onRenewed(verifyData.subscription);
            } else {
              setError(verifyData.message || 'Renewal verification failed. Please contact support.');
            }
          } catch {
            setError('Renewal verification failed. Please contact support.');
          } finally {
            setProcessing(false);
          }
        },
        modal: { ondismiss: () => setProcessing(false) }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError('Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  const expiryDate = user?.subscription?.expiryDate
    ? new Date(user.subscription.expiryDate).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'long', year: 'numeric'
      })
    : null;

  return (
    <div className="sub-renewal-banner">
      <div className="sub-renewal-banner-inner">
        <div className="sub-renewal-banner-icon">⏰</div>
        <div className="sub-renewal-banner-content">
          <h4 className="sub-renewal-banner-title">Subscription Expired</h4>
          <p className="sub-renewal-banner-msg">
            {expiryDate ? `Your access expired on ${expiryDate}. ` : ''}
            Renew to add new competency entries. You can still view your existing submissions and results.
          </p>
          {error && <p className="sub-renewal-banner-error">{error}</p>}
        </div>
        <div className="sub-renewal-banner-actions">
          {settings ? (
            isNativeApp ? (
              <>
                <button
                  className="sub-renewal-banner-btn"
                  onClick={() => window.open(webUrl, '_system')}
                >
                  Renew on Web — ₹{settings.amount?.toLocaleString('en-IN')}
                </button>
                <p style={{ fontSize: 12, color: '#888', margin: '6px 0 0', textAlign: 'center' }}>
                  Pay in your browser, then refresh this app.
                </p>
              </>
            ) : (
              <button
                className="sub-renewal-banner-btn"
                onClick={handleRenew}
                disabled={processing}
              >
                {processing ? (
                  <><span className="sub-renewal-btn-spinner"></span> Processing...</>
                ) : (
                  `Renew — ₹${settings.amount?.toLocaleString('en-IN')}`
                )}
              </button>
            )
          ) : (
            <span style={{ fontSize: 13, color: '#666' }}>Loading renewal details...</span>
          )}
          <button className="sub-renewal-banner-logout" onClick={onLogout}>
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRenewal;
